'use client';

import { useEffect, useRef, type ReactNode } from 'react';

// Native scroll-snap preserves swipe before hydration. Unlike the generic
// carousel, this hero needs neither Embla nor a client copy of its slide data.
export function releaseHeroImages(root: HTMLElement, index: number) {
  const slides = root.querySelectorAll<HTMLElement>('[data-ivato-hero-slide]');
  if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
  for (const i of [index - 1, index, index + 1]) {
    slides[i]?.querySelectorAll('[data-hero-pending]').forEach(source => source.remove());
  }
}

export function HeroEnhancement({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const viewport = root?.querySelector<HTMLElement>('[data-ivato-hero-viewport]');
    if (!root || !viewport) return;
    let frame = 0;
    const index = () => Math.round(viewport.scrollLeft / (viewport.clientWidth || 1));
    const update = () => {
      const current = index();
      releaseHeroImages(root, current);
      root.querySelectorAll<HTMLButtonElement>('[data-hero-go]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.heroGo) === current));
      });
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const onIntent = () => releaseHeroImages(root, index());
    const first = root.querySelector<HTMLImageElement>('[data-hero-first]');
    // Start preparing a neighbour only after the main image has painted.
    const afterImage = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(update);
      });
    };
    first?.addEventListener('load', afterImage);
    first?.addEventListener('error', afterImage);
    if (first?.complete) afterImage();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('pointerdown', onIntent, { passive: true });
    root.addEventListener('focusin', onIntent);
    return () => {
      cancelAnimationFrame(frame);
      first?.removeEventListener('load', afterImage);
      first?.removeEventListener('error', afterImage);
      viewport.removeEventListener('scroll', onScroll);
      viewport.removeEventListener('pointerdown', onIntent);
      root.removeEventListener('focusin', onIntent);
    };
  }, []);

  return (
    <section
      ref={rootRef}
      data-ivato-hero="native"
      className="relative"
      onClick={event => {
        const button = (event.target as Element).closest<HTMLElement>('[data-hero-go]');
        const root = rootRef.current;
        if (!button || !root?.contains(button)) return;
        const viewport = root.querySelector<HTMLElement>('[data-ivato-hero-viewport]');
        const index = Number(button.dataset.heroGo);
        releaseHeroImages(root, index);
        viewport?.scrollTo({
          left: index * viewport.clientWidth,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth',
        });
      }}
    >
      {children}
    </section>
  );
}
